import { useCallback, useEffect, useState } from 'react';
import config from '../config';
import { getSettings, updateSettings } from '../services/SettingsService';
import { PLAN_FEATURES_EVENT, chatInPlan, loadPlanFeatures } from './usePlanFeatures';

/**
 * Chat as a feature (sandbox-first experience, docs/SANDBOX_FIRST.md): whether this person turned Flow's own
 * assistant on. Without `config.sandboxFirst` chat is always on, as in today's Flow.
 *
 * The choice is the person's, kept by their sandbox (`/api/v1/me/settings`, see SettingsService) so it follows them
 * to any browser; localStorage only remembers it between page loads until the sandbox answers. Someone who never
 * chose gets `config.chatDefaultOn` when the organization provides the models (`config.chatOrgModels`), else off.
 *
 * The person's plan decides first (usePlanFeatures `chat`: neither the organization's models nor an own key in the
 * plan): without it chat is off whatever the choice, and every way to turn it on is hidden (`available` false).
 */
const STORAGE_KEY = 'flow.features.chat';
const CHANGE_EVENT = 'flow:chat-feature-changed';

// What the sandbox said, once it answered: `true`/`false`, or `null` for "never chose".
let serverChoice;
let loading = null;

function cached() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'on') { return true; }
    if (stored === 'off') { return false; }
  } catch {
    // Storage blocked: fall back to the default.
  }
  return null;
}

function remember(enabled) {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off');
  } catch {
    // Storage blocked: the sandbox still keeps the choice.
  }
}

function read() {
  if (!config.sandboxFirst) { return true; }
  if (!chatInPlan()) { return false; }
  const choice = serverChoice ?? cached();
  if (choice === true || choice === false) { return choice; }
  return Boolean(config.chatOrgModels && config.chatDefaultOn);
}

/** Loads the person's choice from their sandbox once per page; later calls share the same request. */
export function loadChatChoice() {
  if (!config.sandboxFirst) { return Promise.resolve(); }
  if (!loading) {
    loading = getSettings()
      .then((settings) => {
        const enabled = settings?.chat?.enabled;
        serverChoice = enabled === true || enabled === false ? enabled : null;
        if (serverChoice !== null) { remember(serverChoice); }
        window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
      })
      .catch(() => { loading = null; });
  }
  return loading;
}

export function isChatEnabled() {
  return read();
}

/** Whether the person's plan includes chat (always without sandbox-first; `true` while the plan is unknown). */
export function isChatAvailable() {
  return !config.sandboxFirst || chatInPlan();
}

/** Turns Chat on or off for this person, everywhere. */
export function setChatEnabled(enabled) {
  serverChoice = Boolean(enabled);
  remember(serverChoice);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { enabled: serverChoice } }));
  if (config.sandboxFirst) {
    updateSettings({ chat: { enabled: serverChoice } }).catch(() => {
      // The sandbox could not save it: the choice still holds in this browser.
    });
  }
}

/** Forgets what was loaded (tests). */
export function resetChatChoice() {
  serverChoice = undefined;
  loading = null;
}

/**
 * `[enabled, setEnabled, available]`, kept in sync across components and tabs. `available` false: the person's plan
 * has no chat, so nothing offers to turn it on (`enabled` is false too).
 */
export default function useChatFeature() {
  const [state, setState] = useState(() => ({ enabled: read(), available: isChatAvailable() }));
  useEffect(() => {
    const sync = () => setState({ enabled: read(), available: isChatAvailable() });
    const onStorage = (event) => { if (event.key === STORAGE_KEY) { sync(); } };
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener(PLAN_FEATURES_EVENT, sync);
    window.addEventListener('storage', onStorage);
    loadChatChoice();
    loadPlanFeatures();
    sync();
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener(PLAN_FEATURES_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  const update = useCallback((next) => setChatEnabled(Boolean(next)), []);
  return [state.enabled, update, state.available];
}
